import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  IconButton,
  CircularProgress,
  TextField,
  Button,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import InboxIcon from '@mui/icons-material/Inbox';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { PosSalesSummeryReportAPI } from '../../../component/Modules/PointOfSale/Report/ReportAPI';
import { viewShiftAPICall } from '../../../API/Settings/Shift/viewShiftAPI';
import './mobileReports.css';

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const MobilePosSummary = ({ open, onClose, branchId: propBranchId, branchName: propBranchName }) => {
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [selectedShift, setSelectedShift] = useState(null);
  const [disableShift, setDisableShift] = useState(false);
  const [disableDates, setDisableDates] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    sales: true,
    payments: true
  });
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const printRef = useRef(null);
  const decimalPosition = Number(localStorage.getItem("decimalPosition")) || 2;
  const userInfo = useSelector((state) => state.loginResponseSlice.value);
  const companyProfile = useSelector((state) => state.companyProfileSlice?.userValue);
  const allShiftList = useSelector((state) => state.viewShiftSlice.value);
  const { salesSummeryReport } = useSelector((state) => state.posReportshiftSlice);

  // Use prop branchId if provided, otherwise fall back to userInfo
  const effectiveBranchId = propBranchId || userInfo?.branchPk;

  useEffect(() => {
    if (open && effectiveBranchId) {
      console.log('📡 MobilePosSummary: Fetching shifts for branch:', effectiveBranchId);
      viewShiftAPICall({ branchId: effectiveBranchId });
      setSelectedShift(null); // Reset shift when branch changes
      fetchSummary();
    }
  }, [open, effectiveBranchId]);

  // Debug: Log shift list when it changes
  useEffect(() => {
    console.log('📋 MobilePosSummary: allShiftList updated:', allShiftList);
    if (allShiftList?.length > 0) {
      console.log('📋 MobilePosSummary: First shift object keys:', Object.keys(allShiftList[0]));
      console.log('📋 MobilePosSummary: First shift object:', allShiftList[0]);
    }
  }, [allShiftList]);

  const fetchSummary = async () => {
    if (!effectiveBranchId) return;
    
    setLoading(true);
    try {
      const params = {
        branchId: effectiveBranchId
      };
      
      if (selectedShift?._id) {
        params.shiftId = selectedShift._id;
      } else {
        if (fromDate) params.startDate = `${fromDate} 00:00:00`;
        if (toDate) params.endDate = `${toDate} 23:59:59`;
      }
      
      await PosSalesSummeryReportAPI(params);
    } catch (error) {
      console.error('Error fetching POS summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShiftChange = (event, newValue) => {
    setSelectedShift(newValue);
    if (newValue) {
      setDisableDates(true);
      setFromDate('');
      setToDate('');
    } else {
      setDisableDates(false);
    }
  };

  const handleDateChange = (field, value) => {
    if (field === 'fromDate') setFromDate(value);
    else setToDate(value);
    
    if (value) {
      setDisableShift(true);
      setSelectedShift(null);
    } else {
      setDisableShift(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatAmount = (amount) => {
    return Number(amount || 0).toFixed(decimalPosition);
  };

  const renderSalesSummary = () => {
    const reportData = salesSummeryReport?.data?.[0];
    if (!reportData?.salesTypeWise) return null;

    const mainTypes = ["DINEIN", "DELIVERY", "TAKEAWAY", "RETURN_ORDER"];
    const salesList = reportData.salesTypeWise
      .filter(item => mainTypes.includes(item.ordType))
      .map(item => ({
        ordType: item.ordType,
        label: item.ordType === 'DINEIN' ? 'Dine In' : 
               item.ordType === 'TAKEAWAY' ? 'Take Away' : 
               item.ordType === 'DELIVERY' ? 'Delivery' : 
               item.ordType === 'RETURN_ORDER' ? 'Return Order' : item.ordType,
        count: item.count || 0,
        value: item.netSales || 0
      }));

    const aggregatorBreakup = reportData.aggregatorBreakup || [];

    if (!salesList.length) return null;

    return (
      <div className="mobile-report-card">
        <div 
          className="mobile-report-card-header sales"
          onClick={() => toggleSection('sales')}
        >
          <h4><ReceiptLongIcon /> Sales Type Wise</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="total-badge">{formatAmount(reportData.netSales)}</span>
            {expandedSections.sales ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        </div>
        <div className={`mobile-report-card-content ${expandedSections.sales ? 'expanded' : ''}`}>
          <table className="mobile-report-table">
            <thead>
              <tr>
                <th>Type</th>
                <th className="text-center">Count</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {salesList.map((item, idx) => (
                <React.Fragment key={idx}>
                  <tr>
                    <td>{item.label}</td>
                    <td className="text-center">{item.count}</td>
                    <td className="text-right">{formatAmount(item.value)}</td>
                  </tr>
                  {/* Aggregator breakdown under Delivery */}
                  {item.ordType === 'DELIVERY' && aggregatorBreakup.length > 0 && (
                    aggregatorBreakup.map((agg, aggIdx) => (
                      <tr key={`agg-${aggIdx}`} className="aggregator-row">
                        <td style={{ paddingLeft: '20px', fontStyle: 'italic', color: '#666' }}>
                          ↳ {agg.aggregatorName}
                        </td>
                        <td className="text-center" style={{ color: '#666' }}>{agg.count}</td>
                        <td className="text-right" style={{ color: '#666' }}>{formatAmount(agg.netSales)}</td>
                      </tr>
                    ))
                  )}
                </React.Fragment>
              ))}
              <tr className="total-row">
                <td>Net Sales</td>
                <td className="text-center">{reportData.totalOrders || 0}</td>
                <td className="text-right">{formatAmount(reportData.netSales)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPaymentSummary = () => {
    const reportData = salesSummeryReport?.data?.[0];
    const tenderDetails = reportData?.paymentDetails?.tenderBreakeupDetails;
    if (!tenderDetails?.length) return null;

    const total = reportData?.paymentDetails?.branchPaymentSummary?.totalTransactions || 0;

    return (
      <div className="mobile-report-card">
        <div 
          className="mobile-report-card-header payment"
          onClick={() => toggleSection('payments')}
        >
          <h4><PaymentsIcon /> Tender Breakup</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="total-badge">{formatAmount(total)}</span>
            {expandedSections.payments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        </div>
        <div className={`mobile-report-card-content ${expandedSections.payments ? 'expanded' : ''}`}>
          <table className="mobile-report-table">
            <thead>
              <tr>
                <th>Payment Method</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {tenderDetails.map((payment, idx) => (
                <tr key={idx}>
                  <td>{payment.journalName || payment._id}</td>
                  <td className="text-right">{formatAmount(payment.totalAmount)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>Total</td>
                <td className="text-right">{formatAmount(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderGrandTotal = () => {
    const reportData = salesSummeryReport?.data?.[0];
    if (!reportData) return null;

    const grossSales = reportData.grossSales || 0;
    const netSales = reportData.netSales || 0;
    const totalOrders = reportData.totalOrders || 0;
    const avgBillValue = reportData.avgBillValue || 0;

    return (
      <div className="mobile-summary-grid">
        <div className="summary-item">
          <div className="label">Gross Sales</div>
          <div className="value" style={{ color: '#11998e' }}>{formatAmount(grossSales)}</div>
        </div>
        <div className="summary-item">
          <div className="label">Net Sales</div>
          <div className="value" style={{ color: '#f5576c' }}>{formatAmount(netSales)}</div>
        </div>
        <div className="summary-item">
          <div className="label">Total Orders</div>
          <div className="value" style={{ color: '#667eea' }}>{totalOrders}</div>
        </div>
        <div className="summary-item">
          <div className="label">Avg Bill Value</div>
          <div className="value" style={{ color: '#764ba2' }}>{formatAmount(avgBillValue)}</div>
        </div>
      </div>
    );
  };

  // Render all statistics like normal report
  const renderStatistics = () => {
    const reportData = salesSummeryReport?.data?.[0];
    if (!reportData) return null;

    const stats = [
      { label: 'Gross Sales', value: formatAmount(reportData.grossSales) },
      { label: 'Net Sales', value: formatAmount(reportData.netSales) },
      { label: 'Total Transaction', value: formatAmount(reportData.paymentDetails?.branchPaymentSummary?.totalTransactions || 0) },
      { label: 'Average Bill Value', value: formatAmount(reportData.avgBillValue) },
      { label: 'Wallet', value: formatAmount(reportData.paymentDetails?.wallet?.walletTotal || 0) },
      { label: 'Total Gross Receipt', value: formatAmount((reportData.grossSales || 0) + (reportData.paymentDetails?.wallet?.walletTotal || 0)) },
      { label: 'Total No Of Orders', value: reportData.totalOrders || 0 },
      { label: 'APC (%)', value: (reportData.apc || 0).toFixed(2) },
      { label: 'No Of Paying Customers', value: reportData.payingCustomers || 0 },
      { label: 'No Of Transactions', value: reportData.paymentDetails?.branchPaymentSummary?.totalTransactionCount || 0 },
      { label: 'Items Sold', value: reportData.itemsSold || 0 },
      { label: 'No of Refunds', value: reportData.refundCount || 0, highlight: reportData.refundCount > 0 ? 'warning' : null },
      { label: 'No of Cancels', value: reportData.cancelCount || 0, highlight: reportData.cancelCount > 0 ? 'danger' : null },
    ];

    return (
      <div className="mobile-report-card">
        <div className="mobile-report-card-header sales">
          <h4><ReceiptLongIcon /> Statistics</h4>
        </div>
        <div className="mobile-report-card-content expanded">
          <table className="mobile-report-table">
            <tbody>
              {stats.map((stat, idx) => (
                <tr key={idx} className={stat.highlight ? `highlight-${stat.highlight}` : ''}>
                  <td>{stat.label}</td>
                  <td className="text-right">{stat.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Delivery Stats */}
          {reportData.deliveryStats?.totalDeliveryCharge > 0 && (
            <div className="mobile-stat-highlight delivery">
              <span>Delivery ({reportData.deliveryStats?.deliveryCount || 0} orders | {(reportData.deliveryStats?.totalKm || 0).toFixed(1)} KM)</span>
              <span>{formatAmount(reportData.deliveryStats?.totalDeliveryCharge)}</span>
            </div>
          )}
          
          {/* Payment Pending */}
          {reportData.pendingOrdersCount > 0 && (
            <div className="mobile-stat-highlight pending">
              <span>Payment Pending ({reportData.pendingOrdersCount} orders)</span>
              <span>{formatAmount(reportData.pendingOrdersAmount)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // PDF Generation
  const generatePdfPreview = async () => {
    if (!printRef.current) return;
    
    setPdfLoading(true);
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      const blob = pdf.output('blob');
      const dataUrl = URL.createObjectURL(blob);
      setPdfBlob(blob);
      setPdfDataUrl(dataUrl);
      setShowPdfPreview(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `@page { size: A4; margin: 10mm; }`
  });

  const sharePDF = async () => {
    if (!pdfBlob) return;
    const fileName = `SalesSummary_${fromDate}_to_${toDate}.pdf`;
    
    if (navigator.share && navigator.canShare) {
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: 'Sales Summary Report' });
          return;
        } catch (err) {
          if (err.name !== 'AbortError') console.error('Share failed:', err);
        }
      }
    }
    downloadPDF();
  };

  const downloadPDF = () => {
    if (!pdfDataUrl) return;
    const link = document.createElement('a');
    link.href = pdfDataUrl;
    link.download = `SalesSummary_${fromDate}_to_${toDate}.pdf`;
    link.click();
  };

  const closePdfPreview = () => {
    setShowPdfPreview(false);
    if (pdfDataUrl) URL.revokeObjectURL(pdfDataUrl);
    setPdfDataUrl(null);
    setPdfBlob(null);
  };

  // Render Professional A4 Print Template
  const renderPrintTemplate = () => {
    const reportData = salesSummeryReport?.data?.[0];
    if (!reportData) return null;

    const rowStyle = (isAlt) => ({
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 15px',
      backgroundColor: isAlt ? '#f5f5f5' : '#fff',
      borderBottom: '1px solid #eee'
    });

    const sectionHeader = {
      backgroundColor: '#333',
      color: 'white',
      padding: '10px 15px',
      fontWeight: 'bold',
      marginTop: '15px'
    };

    return (
      <div ref={printRef} style={{ width: '210mm', padding: '20px', backgroundColor: 'white', fontFamily: 'Arial, sans-serif' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: '2px solid #333', paddingBottom: '15px' }}>
          {companyProfile?.logo && (
            <img src={companyProfile.logo} alt="Logo" style={{ height: '50px', marginBottom: '10px' }} />
          )}
          <h2 style={{ margin: '0 0 5px 0' }}>{companyProfile?.companyName || 'Restaurant'}</h2>
          <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
            {companyProfile?.companyAddress || ''}
          </p>
          <h3 style={{ margin: '15px 0 5px 0' }}>Sales Summary Report</h3>
          <p style={{ margin: 0, fontSize: '12px' }}>
            <strong>Branch:</strong> {reportData.branchName}
            {reportData.shiftNumber ? (
              <> | <strong>Shift:</strong> {reportData.shiftNumber}</>
            ) : (
              <> | <strong>Period:</strong> {fromDate} to {toDate}</>
            )}
          </p>
          {reportData.shiftStartTime && (
            <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#666' }}>
              <strong>Shift Start:</strong> {new Date(reportData.shiftStartTime).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
              {reportData.shiftEndTime && (
                <> | <strong>Shift End:</strong> {new Date(reportData.shiftEndTime).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</>
              )}
            </p>
          )}
        </div>

        {/* Statistics */}
        <div style={rowStyle(false)}><span>Gross Sales</span><span>{formatAmount(reportData.grossSales)}</span></div>
        <div style={rowStyle(true)}><span>Net Sales</span><span>{formatAmount(reportData.netSales)}</span></div>
        <div style={rowStyle(false)}><span>Total Transaction</span><span>{formatAmount(reportData.paymentDetails?.branchPaymentSummary?.totalTransactions || 0)}</span></div>
        <div style={rowStyle(true)}><span>Average Bill Value</span><span>{formatAmount(reportData.avgBillValue)}</span></div>
        <div style={rowStyle(false)}><span>Wallet</span><span>{formatAmount(reportData.paymentDetails?.wallet?.walletTotal || 0)}</span></div>
        <div style={rowStyle(true)}><span>Total Gross Receipt</span><span>{formatAmount((reportData.grossSales || 0) + (reportData.paymentDetails?.wallet?.walletTotal || 0))}</span></div>
        <div style={rowStyle(false)}><span>Total No Of Orders</span><span>{reportData.totalOrders}</span></div>
        <div style={rowStyle(true)}><span>APC (%)</span><span>{(reportData.apc || 0).toFixed(2)}</span></div>
        <div style={rowStyle(false)}><span>No Of Paying Customers</span><span>{reportData.payingCustomers || 0}</span></div>
        <div style={rowStyle(true)}><span>No Of Transactions</span><span>{reportData.paymentDetails?.branchPaymentSummary?.totalTransactionCount || 0}</span></div>
        <div style={rowStyle(false)}><span>Items Sold</span><span>{reportData.itemsSold || 0}</span></div>
        <div style={rowStyle(true)}><span>No of Refunds</span><span>{reportData.refundCount || 0}</span></div>
        <div style={rowStyle(false)}><span>No of Cancels</span><span>{reportData.cancelCount || 0}</span></div>

        {/* Delivery Stats */}
        {reportData.deliveryStats?.totalDeliveryCharge > 0 && (
          <div style={{ backgroundColor: '#e3f2fd', padding: '8px 15px', display: 'flex', justifyContent: 'space-between', borderLeft: '4px solid #2196f3' }}>
            <span style={{ fontWeight: 'bold', color: '#1565c0' }}>Delivery ({reportData.deliveryStats?.deliveryCount || 0} orders | {(reportData.deliveryStats?.totalKm || 0).toFixed(1)} KM)</span>
            <span style={{ fontWeight: 'bold', color: '#1565c0' }}>{formatAmount(reportData.deliveryStats?.totalDeliveryCharge)}</span>
          </div>
        )}

        {/* Payment Pending */}
        {reportData.pendingOrdersCount > 0 && (
          <div style={{ backgroundColor: '#fff3cd', padding: '8px 15px', display: 'flex', justifyContent: 'space-between', borderLeft: '4px solid #ffc107' }}>
            <span style={{ fontWeight: 'bold', color: '#856404' }}>Payment Pending ({reportData.pendingOrdersCount} orders)</span>
            <span style={{ fontWeight: 'bold', color: '#856404' }}>{formatAmount(reportData.pendingOrdersAmount)}</span>
          </div>
        )}

        {/* Sales Type Wise */}
        <div style={sectionHeader}>SALES TYPE WISE BREAKUP DETAILS</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 15px', backgroundColor: '#ddd', fontWeight: 'bold' }}>
          <span style={{ width: '40%' }}>Type</span>
          <span style={{ width: '30%', textAlign: 'center' }}>Count</span>
          <span style={{ width: '30%', textAlign: 'right' }}>Amount</span>
        </div>
        {reportData.salesTypeWise?.filter(item => ["DINEIN", "DELIVERY", "TAKEAWAY", "RETURN_ORDER"].includes(item.ordType)).map((item, idx) => (
          <React.Fragment key={idx}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 15px', backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5' }}>
              <span style={{ width: '40%' }}>{item.ordType === 'DINEIN' ? 'Dine In' : item.ordType === 'TAKEAWAY' ? 'Take Away' : item.ordType === 'DELIVERY' ? 'Delivery' : item.ordType === 'RETURN_ORDER' ? 'Return Order' : item.ordType}</span>
              <span style={{ width: '30%', textAlign: 'center' }}>{item.count}</span>
              <span style={{ width: '30%', textAlign: 'right' }}>{formatAmount(item.netSales)}</span>
            </div>
            {/* Aggregator breakdown under Delivery */}
            {item.ordType === 'DELIVERY' && reportData.aggregatorBreakup?.length > 0 && (
              reportData.aggregatorBreakup.map((agg, aggIdx) => (
                <div key={`agg-${aggIdx}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 15px', paddingLeft: '45px', backgroundColor: '#f0f0f0', fontSize: '0.85em', fontStyle: 'italic', color: '#555' }}>
                  <span style={{ width: '40%' }}>↳ {agg.aggregatorName}</span>
                  <span style={{ width: '30%', textAlign: 'center' }}>{agg.count}</span>
                  <span style={{ width: '30%', textAlign: 'right' }}>{formatAmount(agg.netSales)}</span>
                </div>
              ))
            )}
          </React.Fragment>
        ))}

        {/* Tender Breakup */}
        <div style={sectionHeader}>TENDER BREAKUP DETAILS</div>
        {reportData.paymentDetails?.tenderBreakeupDetails?.map((item, idx) => (
          <div key={idx} style={rowStyle(idx % 2 !== 0)}>
            <span>{item.journalName}</span>
            <span>{formatAmount(item.totalAmount)}</span>
          </div>
        ))}

        {/* Footer */}
        <div style={{ marginTop: '30px', textAlign: 'center', fontSize: '10px', color: '#888', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
          Generated on {new Date().toLocaleString('en-IN')}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="mobile-report-dialog"
    >
      <div className="mobile-report-header">
        <h3>POS Sales Summary</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {salesSummeryReport?.data?.length > 0 && (
            <IconButton 
              onClick={generatePdfPreview} 
              size="small" 
              disabled={pdfLoading}
              title="PDF Preview"
            >
              {pdfLoading ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
            </IconButton>
          )}
          <IconButton className="close-btn" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <div className="mobile-report-container">
        {/* Filter Section */}
        <div className="mobile-report-filters">
          {/* Shift Selector */}
          <div className="filter-row">
            <Autocomplete
              options={allShiftList || []}
              getOptionLabel={(option) => option?.shiftSeq || option?.SHIFID || ''}
              value={selectedShift}
              onChange={handleShiftChange}
              disabled={disableShift}
              size="small"
              fullWidth
              isOptionEqualToValue={(option, value) => option?._id === value?._id}
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  {option?.shiftSeq || option?.SHIFID || 'Shift'}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Shift" InputLabelProps={{ shrink: true }} />
              )}
            />
          </div>
          <div className="filter-row">
            <TextField
              label="From Date"
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => handleDateChange('fromDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={disableDates}
            />
            <TextField
              label="To Date"
              type="date"
              size="small"
              value={toDate}
              onChange={(e) => handleDateChange('toDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={disableDates}
            />
          </div>
          <div className="filter-row" style={{ justifyContent: 'flex-end' }}>
            <Button
              className="submit-btn"
              onClick={fetchSummary}
              disabled={loading}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="mobile-report-loading">
            <CircularProgress size={40} />
            <p>Loading summary...</p>
          </div>
        ) : !salesSummeryReport?.data?.length ? (
          <div className="mobile-report-empty">
            <InboxIcon />
            <p>No data found.<br />Try adjusting the date range.</p>
          </div>
        ) : (
          <>
            {renderGrandTotal()}
            {renderStatistics()}
            {renderSalesSummary()}
            {renderPaymentSummary()}
          </>
        )}
      </div>

      {/* PDF Preview Dialog */}
      <Dialog
        open={showPdfPreview}
        onClose={closePdfPreview}
        fullWidth
        maxWidth="md"
        className="pdf-preview-dialog"
      >
        <div className="pdf-preview-header">
          <h3>Print Preview</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconButton onClick={handlePrint} size="small" title="Print">
              <PrintIcon />
            </IconButton>
            <IconButton onClick={sharePDF} size="small" title="Share">
              <ShareIcon />
            </IconButton>
            <IconButton onClick={downloadPDF} size="small" title="Download">
              <PictureAsPdfIcon />
            </IconButton>
            <IconButton onClick={closePdfPreview} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <div className="pdf-preview-content">
          {pdfDataUrl && (
            <iframe src={pdfDataUrl} title="PDF Preview" className="pdf-iframe" />
          )}
        </div>
      </Dialog>

      {/* Hidden Professional A4 Print Template */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        {renderPrintTemplate()}
      </div>
    </Dialog>
  );
};

export default MobilePosSummary;
